/**
 * @file supabase.config.ts
 * @description Standalone Supabase client configuration for TableGreet Chef Web.
 * 
 * ARCHITECTURAL RESPONSIBILITIES:
 * 1. Exclusively routes through the TableGreet API gateway (https://api.tablegreet.com).
 * 2. Uses the worker-injected key sentinel ('WORKER_INJECTED') to protect credentials.
 * 3. Configures auth session storage and real-time subscription channels for kitchen orders.
 */

import { createClient } from '@supabase/supabase-js';

const SENTINEL = 'WORKER_INJECTED';
const LOCAL_DEV_API_PROXY_PATH = '/__tablegreet-api';

const isLocalDev = typeof window !== 'undefined' && 
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

// In local dev, use the Vite forwarder to prevent CORS and browser security warnings
const resolvedUrl = isLocalDev 
  ? `${window.location.origin}${LOCAL_DEV_API_PROXY_PATH}`
  : (import.meta.env.VITE_API_URL || 'https://api.tablegreet.com');

const resolvedAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || SENTINEL;

// Robust storage wrapper with sessionStorage fallback when localStorage is restricted or quota exceeded
const tgChefStorage = {
  getItem: (key: string): string | null => {
    if (typeof window === 'undefined') return null;
    try {
      return localStorage.getItem(key) || sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem: (key: string, value: string): void => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(key, value);
    } catch {
      try {
        sessionStorage.setItem(key, value);
      } catch {
        // Suppresses storage quota and permission restrictions in strict browser modes
      }
    }
  },
  removeItem: (key: string): void => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    } catch {
      // Suppresses storage cleanup exceptions
    }
  },
};

// Standalone authenticated Supabase client for kitchen operators
export const supabase = createClient(resolvedUrl, resolvedAnonKey, {
  auth: {
    storage: tgChefStorage,
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storageKey: 'tg_chef_auth_session',
    // Bypasses navigator.locks to prevent lock-acquisition timeouts and race conditions in browser environments
    lock: (_name: string, _acquireTimeout: number, fn: () => Promise<any>) => fn(),
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
});

